import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiTooltip } from '../../Overlay/NoddiTooltip';
import {
  ColorPicker,
  ColorPickerAlphaSlider,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerEyeDropper,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger
} from './ColorPicker';

const PRESET_COLORS = [
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#EAB308',
  '#84CC16',
  '#22C55E',
  '#10B981',
  '#14B8A6',
  '#06B6D4',
  '#0EA5E9',
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#D946EF',
  '#EC4899',
  '#F43F5E',
  '#64748B',
  '#78716C'
];

interface Props {
  value?: string | null;
  onChange: (value: string) => void;
  /** Shows an inline clear (X) while a color is set. */
  onClear?: () => void;
  label?: string;
  tooltip?: string;
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export const NoddiColorPicker = ({ value, onChange, onClear, label, tooltip, id, ...aria }: Props) => {
  return (
    <div className='flex flex-col gap-0.5'>
      {label && (
        <div className='flex items-center gap-2'>
          <label htmlFor={id} className='text-sm'>
            {label}
          </label>
          {tooltip && <NoddiTooltip content={tooltip} />}
        </div>
      )}
      <ColorPicker value={value ?? undefined} onValueChange={onChange} format='hex'>
        <ColorPickerTrigger asChild>
          <button
            type='button'
            id={id}
            {...aria}
            className={cn(
              'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors',
              'hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden',
              !value && 'text-muted-foreground'
            )}
          >
            {value ? (
              <ColorPickerSwatch className='size-5 rounded-md border' />
            ) : (
              <span className='size-5 rounded-md border border-dashed' />
            )}
            <span className='flex-1 text-left'>{value ?? 'Pick a color'}</span>
            {value && onClear && (
              // Not a real <button>: invalid inside the trigger button (same as SelectBase).
              <span
                role='button'
                tabIndex={0}
                aria-label={label ? `Clear ${label}` : 'Clear color'}
                onClick={(event) => {
                  event.stopPropagation();
                  onClear();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    onClear();
                  }
                }}
                className='flex items-center rounded-xs text-muted-foreground hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring focus-visible:outline-hidden'
              >
                <NoddiIcon name='X' />
              </span>
            )}
          </button>
        </ColorPickerTrigger>
        <ColorPickerContent className='w-64'>
          <div className='flex flex-col gap-4'>
            <ColorPickerArea />
            <ColorPickerHueSlider />
            <ColorPickerAlphaSlider />
            <div className='flex items-center gap-2'>
              <ColorPickerEyeDropper />
              <ColorPickerInput />
            </div>
            <div className='flex flex-wrap gap-1.5'>
              {PRESET_COLORS.map((hex) => (
                <button
                  key={hex}
                  type='button'
                  aria-label={hex}
                  onClick={() => onChange(hex)}
                  className={cn(
                    'size-6 rounded-md border-2 shadow-xs transition-all',
                    value === hex ? 'scale-110 border-foreground' : 'border-transparent hover:scale-110'
                  )}
                  style={{ backgroundColor: hex }}
                />
              ))}
            </div>
          </div>
        </ColorPickerContent>
      </ColorPicker>
    </div>
  );
};
